import { useState, type ReactNode } from "react";
import { Check, Copy } from "lucide-react";
import type { ReminderLevel } from "@navibiz/chat-core";
import styles from "./Misc.module.css";

export function PageHeader({ kicker, title, description, actions }: { kicker?: ReactNode; title: ReactNode; description?: ReactNode; actions?: ReactNode }) {
  return (
    <header className={styles.pageHeader}>
      <div className={styles.pageTitles}>
        {kicker && <div className="kicker">{kicker}</div>}
        <h1 className={styles.pageTitle}>{title}</h1>
        {description && <p className={styles.pageDescription}>{description}</p>}
      </div>
      {actions && <div className={styles.pageActions}>{actions}</div>}
    </header>
  );
}

export function Segmented<T extends string | number>({ value, options, onChange, label }: { value: T; options: { value: T; label: string }[]; onChange: (value: T) => void; label: string }) {
  return (
    <div className={styles.segmented} role="radiogroup" aria-label={label}>
      {options.map((option) => (
        <button key={String(option.value)} type="button" role="radio" aria-checked={option.value === value} onClick={() => onChange(option.value)}>
          {option.label}
        </button>
      ))}
    </div>
  );
}

export function CopyButton({ text, label = "Salin" }: { text: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      className={styles.copy}
      onClick={async () => {
        await navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 1600);
      }}
    >
      {copied ? <Check aria-hidden /> : <Copy aria-hidden />}
      {copied ? "Tersalin" : label}
    </button>
  );
}

export function CodeBlock({ code, copy = true }: { code: string; copy?: boolean }) {
  return (
    <div className={styles.code}>
      <pre>
        <code>{code}</code>
      </pre>
      {copy && <CopyButton text={code} />}
    </div>
  );
}

const METER_TONE: Record<ReminderLevel, string> = { ok: "good", low: "warning", critical: "serious", empty: "critical" };

/**
 * Saldo runway: the fill carries severity, the track is a lighter step of the
 * same hue, so state reads across the whole bar. Paired with a LevelBadge
 * wherever it appears — colour is never the only signal.
 */
export function Meter({ ratio, level, label }: { ratio: number; level: ReminderLevel; label: string }) {
  const clamped = Math.max(0, Math.min(1, ratio));
  return (
    <div className={`${styles.meter} ${styles[METER_TONE[level]]}`} role="meter" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(clamped * 100)} aria-label={label}>
      <span style={{ width: `${Math.max(clamped * 100, clamped > 0 ? 3 : 0)}%` }} />
    </div>
  );
}

export function Kbd({ children }: { children: ReactNode }) {
  return <kbd className={styles.kbd}>{children}</kbd>;
}
